import { useEffect, useRef, useState } from 'react'
import { Skeleton, Center, Text, Stack, Button } from '@mantine/core'
import ErrorBoundary from '../_ErrorBoundary'
import { EXPANDED_MAP_Z } from '../explore/_expandUrl'
import { trackMapCrash, trackMapReload } from '../_analytics'
import { mapCrashContext, detectUncleanReload } from '../_mapCrashContext'

// A React error, reduced to the enum the analytics contract allows. Never
// returns the input and never keeps more of it than the matching below — the
// message is a local that dies in this function, exactly as
// normalizeMapErrorReason does for MapLibre's own errors.
export function classifyBoundaryError(error) {
  // Only a string or a real `message`. `String(someObject)` yields
  // "[object Object]", which is text, which would classify as `render` — an
  // error carrying no information at all reported as though it carried some.
  const message = typeof error === 'string' ? error : String(error?.message ?? '')
  // WebGL first: on a phone this is the one that means "the GPU took the
  // context away", and it makes every later error moot.
  if (/webgl|context lost|contextlost|gl_|gpu/i.test(message)) return 'webgl'
  // A lazily-imported chunk that arrives broken throws during render rather
  // than rejecting the import, so it lands here and not in the catch below.
  if (/chunk|dynamically imported|importing a module script/i.test(message)) return 'chunk'
  if (message) return 'render'
  return 'unknown'
}

// Shown when the map chunk fails to load (network) or the map component throws
// while rendering. English is hardcoded for the same reason as _ErrorBoundary's
// fallback (rare, context-free last resort).
//
// `onRetry` remounts the map subtree rather than reloading the page, and for a
// GPU failure that is the CORRECT recovery rather than a lesser one: a fresh
// mount builds a fresh canvas and a fresh WebGL context, which is the thing
// that died. It also keeps the visitor's scroll position and the rest of the
// page, where a reload throws both away. Absent (the chunk never arrived),
// there is nothing to remount and the panel stays as it was.
function MapFallback({ height, onRetry = null }) {
  return (
    <Center
      h={height}
      style={{
        background: 'var(--mantine-color-default-hover)',
        borderRadius: 'var(--mantine-radius-md)',
      }}
    >
      <Stack align="center" gap="xs">
        <Text c="dimmed" size="sm">
          The map could not be loaded.
        </Text>
        {onRetry ? (
          <Button size="xs" variant="default" onClick={onRetry}>
            Reload map
          </Button>
        ) : null}
      </Stack>
    </Center>
  )
}

// How early to start loading: begin the fetch when the placeholder is within
// this much of the viewport, so a scrolling visitor meets a drawn map rather
// than a skeleton. Roughly one tall phone screen of runway.
const PRELOAD_MARGIN = '800px'

// Client-only maplibre loader shared by the station and explore maps. maplibre
// touches window/WebGL at import time, so the map module (and its CSS) are
// dynamically imported only after hydration; a skeleton renders during SSR and
// initial load. `load` is the caller's `() => import('./SomeMap')` thunk (kept
// at the call site so Vite can statically analyze it); remaining props pass
// through to the map component.
//
// The import waits for the map to come NEAR the viewport, not merely for
// hydration. On a station page the map sits about twelve screens down (measured
// 9049px into an 11583px mobile page), and loading it on mount put maplibre
// (250KB of JS, ~5.6s of scripting on a throttled Moto G) and the overlay's
// global raster frames — 3.5MB on 2026-08-03, mostly the 3600x1800 MFWAM waves
// PNG — on the critical path of a page whose LCP element is the <h1>. It cost
// the mobile station page ~10s of LCP: 14.2s measured, 4.1s with those frames
// blocked. Above-the-fold maps (home, explore) intersect immediately, so they
// still load right away.
// `onLoadFailure` is optional telemetry, called at most once when the map chunk
// itself never arrives. It is destructured out rather than passed through: it is
// for the page that owns this placeholder, and the map component it never
// managed to load has no use for it. Nothing about the fallback UI depends on
// it, and a throw inside it cannot reach the render — see the call site.
// `surface` names WHICH map died, and it is the one property that decides what
// to do about it — /explore maximized on a phone is a different problem from a
// station page's square inset. Clamped in trackMapCrash, so a caller that
// forgets it reports nothing rather than reporting a lie. It is not passed
// through to the map component.
//
// `fullscreen` says the map will OPEN filling the viewport — a direct hit on
// `/explore`, which is the home page with its map already expanded. It changes
// two things and both are about the placeholder rather than the map:
//
//   * load now, without waiting to intersect. The home map sits about three
//     thousand pixels down the page, so the observer below would not fire until
//     the reader scrolled — and there is nothing to scroll, because the map is
//     supposed to be covering the page already.
//   * reserve the whole screen for the skeleton, so the wait looks like a map
//     arriving rather than the home page painting and then being covered.
//
// It is read once, from the page's own server prop. Collapsing is a state
// change inside the map, by which time this component has long since handed off.
export default function LazyMap({
  load,
  height = '60vh',
  onLoadFailure = null,
  surface = 'explore',
  fullscreen = false,
  ...props
}) {
  const [MapComponent, setMapComponent] = useState(null)
  const [failed, setFailed] = useState(false)
  const [near, setNear] = useState(fullscreen)
  const placeholder = useRef(null)
  const reportedFailure = useRef(false)

  // Did the last visit to this tab end with a map running and no goodbye?
  //
  // Checked on MOUNT, before anything arms the marks again, and exactly once
  // per mount — the detector clears the marks as it reads them, so a second
  // call returns nothing rather than double-reporting one kill. See
  // detectUncleanReload for what else can look like this; it is evidence, not
  // proof, and the alternative is no signal at all.
  useEffect(() => {
    if (detectUncleanReload()) trackMapReload(surface)
    // Mount only: a re-render must not re-ask a question whose evidence has
    // already been consumed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Watch the placeholder until it approaches the viewport. No observer (very
  // old browsers, jsdom) means load straight away — the old behavior, which is
  // slow but never wrong.
  useEffect(() => {
    if (near) return undefined
    const el = placeholder.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setNear(true)
      return undefined
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true)
          observer.disconnect()
        }
      },
      { rootMargin: PRELOAD_MARGIN },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [near])

  useEffect(() => {
    if (!near) return undefined
    let active = true
    Promise.all([
      load(),
      import('maplibre-gl/dist/maplibre-gl.css'),
      import('./_maplibreWorkerSetup'),
    ])
      .then(([mod]) => {
        if (active) setMapComponent(() => mod.default)
      })
      .catch((err) => {
        // A rejected dynamic import is a promise rejection, not a render error,
        // so the boundary below can't catch it — handle it here.
        console.error('[LazyMap] map failed to load', err)
        if (active) setFailed(true)
        // Reported whether or not this mount is still active: the chunk not
        // arriving is a fact about the visit, not about this component's
        // lifetime. Latched, and wrapped, because telemetry must never be the
        // reason a fallback panel does not render.
        if (!reportedFailure.current) {
          reportedFailure.current = true
          try {
            onLoadFailure?.()
          } catch {
            // See above.
          }
        }
      })
    return () => {
      active = false
    }
    // load is a fresh thunk each render; only load once, once we're near.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [near])

  if (failed) {
    return <MapFallback height={height} />
  }
  if (!MapComponent) {
    return (
      <Skeleton
        ref={placeholder}
        height={height}
        radius="md"
        animate={false}
        style={
          fullscreen
            ? // The same geometry mapRootStyle gives the maximized map, so the
              // skeleton stands exactly where the map is about to.
              {
                position: 'fixed',
                inset: 0,
                width: '100%',
                height: '100dvh',
                zIndex: EXPANDED_MAP_Z,
              }
            : undefined
        }
      />
    )
  }
  // A render throw in the map (bad snapshot shape, WebGL context loss) degrades
  // to the fallback panel instead of blanking the whole page — and now says so
  // out loud, and offers a way back.
  return (
    <ErrorBoundary
      onError={(error) => trackMapCrash(surface, classifyBoundaryError(error), mapCrashContext())}
      fallback={({ retry }) => <MapFallback height={height} onRetry={retry} />}
    >
      <MapComponent {...props} />
    </ErrorBoundary>
  )
}
